import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Card, CardContent, Typography, IconButton, Button, CircularProgress,
  Alert, Divider, Chip, Grid, Tooltip, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import { ArrowBack, ArrowForward, OpenInNew, Download, Add, AttachFile, ExpandMore, Edit } from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { formatDate } from '../lib/dateUtils';
import { exportRowsToPdf } from '../lib/exportUtils';
import { arabicColumnLabel, arabicEntityLabel } from '../lib/columnLabels';
import { enumChip } from '../lib/enumLabels';
import DetailDialog from '../components/DetailDialog';
import { getEntityDraftFields } from '../lib/entityForms';
import SafeHtml from '../components/agent/SafeHtml';

// Config per entity: the table, a title field, and a select string that resolves
// common foreign keys to readable names. Keeps each detail page consistent.
interface EntityConfig {
  table: string;
  titleField: string;
  select?: string;
  // Fields that are URLs/attachments shown as a download/open link.
  linkFields?: string[];
  // The most important fields, shown prominently first. Everything else on
  // the record renders below, under an "additional details" divider. When
  // omitted, all fields render flat in a single section (original behavior).
  coreFields?: string[];
  // Fields folded into the attachments accordion instead of the plain field
  // grid (e.g. a single legacy "attachment" URL column alongside a richer
  // multi-attachment table).
  fileFields?: string[];
  // Fields storing sanitized rich-text HTML (edited via RichTextEditor) —
  // rendered formatted via SafeHtml instead of as raw/plain text.
  htmlFields?: string[];
  // A related, per-record attachment table shown inline instead of as its own
  // top-level nav destination (e.g. tender_attachments -> tenders.id).
  attachments?: {
    table: string;
    parentField: string;
    fileField: string;
    labelField: string;
    // When set, the parent value comes from this field ON THE RECORD instead of
    // the record's own id (e.g. news photos are linked by album_id).
    parentValueField?: string;
  };
}

const ENTITY_CONFIG: Record<string, EntityConfig> = {
  partners: {
    table: 'partners',
    titleField: 'name',
    select: '*, companies:company_id (id, name, pcu_id)',
  },
  tenders: {
    table: 'tenders',
    titleField: 'tender_name',
    linkFields: ['link'],
    fileFields: ['attachment'],
    coreFields: [
      'tender_name', 'reference_no', 'authority_dept', 'owner_name', 'type',
      'area', 'date', 'submission_deadline', 'submission_time', 'insurance_value', 'currency',
    ],
    attachments: {
      table: 'tender_attachments',
      parentField: 'tender_id',
      fileField: 'file_url',
      labelField: 'filename',
    },
  },
  contracts: {
    table: 'contracts',
    titleField: 'name',
    select: '*, companies:contractor_id (id, name, pcu_id), tenders:tender_id (id, tender_name)',
  },
  news: {
    table: 'news',
    titleField: 'title',
    // pull the referenced album so it is visible on the detail page
    select: '*, photo_albums:album_id (id, name, cover_image_url)',
    linkFields: ['source_url'],
    coreFields: ['title', 'date', 'is_public', 'source', 'album_id', 'body'],
    htmlFields: ['body'],
    // the album's photos, shown inline
    attachments: {
      table: 'gallery_photos',
      parentField: 'album_id',
      parentValueField: 'album_id',
      fileField: 'image_url',
      labelField: 'source_photo_url',
    },
  },
  trainings: {
    table: 'trainings',
    titleField: 'title',
    linkFields: ['source_url'],
    fileFields: ['image_url', 'flyer_url'],
    coreFields: [
      'title', 'date', 'registration_deadline', 'location', 'instructor_name',
      'cost', 'cost_currency', 'status', 'is_public',
    ],
  },
  fund_system: {
    table: 'fund_system',
    titleField: 'subscription_number',
    select: '*, partners:partner_id (id, name), companies:company_id (id, name, pcu_id)',
  },
};

const HIDDEN_FIELDS = new Set(['id']);
const isDateField = (k: string) => k.includes('date') || k.endsWith('_at') || k.endsWith('Date');
const prettyHeader = (k: string) => k.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
const isPdfUrl = (v: string) => /\.pdf(\?|$)/i.test(v);
const isImageUrl = (v: string) => /\.(png|jpe?g|gif|webp)(\?|$)/i.test(v);

const EntityDetailPage: React.FC<{ entity: keyof typeof ENTITY_CONFIG }> = ({ entity }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [record, setRecord] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);

  const config = ENTITY_CONFIG[entity];
  const BackIcon = isRTL ? ArrowForward : ArrowBack;

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: err } = await supabase
          .from(config.table)
          .select(config.select || '*')
          .eq('id', id)
          .single();
        if (err) throw err;
        if (active) setRecord(data);
      } catch (e: any) {
        if (active) setError(e?.message || 'Failed to load record');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [entity, id, config.table, config.select]);

  const fetchAttachments = useCallback(async () => {
    if (!config.attachments || !id) return;
    const { table, parentField, parentValueField } = config.attachments;
    const parentValue = parentValueField ? record?.[parentValueField] : id;
    if (!parentValue) { setAttachments([]); return; }
    const { data, error: err } = await supabase
      .from(table)
      .select('*')
      .eq(parentField, parentValue)
      .order('created_at', { ascending: false });
    if (!err) setAttachments(data || []);
  }, [config.attachments, id, record]);

  useEffect(() => { fetchAttachments(); }, [fetchAttachments]);

  const entityLabel = t(`nav.${entity}`, { defaultValue: prettyHeader(entity) });

  const renderValue = (key: string, value: any): React.ReactNode => {
    if (value === null || value === undefined || value === '') return <Typography variant="body2" color="text.disabled">—</Typography>;

    // Rich-text HTML field (edited via RichTextEditor) -> render formatted.
    if (config.htmlFields?.includes(key) && typeof value === 'string') {
      return <SafeHtml html={value} />;
    }

    // Resolved foreign-key object (e.g. companies:company_id) -> linked chip.
    if (typeof value === 'object' && !Array.isArray(value)) {
      const fkName = value.name || value.tender_name || value.title;
      if (fkName && value.id) {
        const fkEntity = key === 'tenders' ? 'tenders' : key === 'partners' ? 'partners' : 'companies';
        const route = fkEntity === 'companies' ? `/companies/${value.id}` : `/${fkEntity}/${value.id}`;
        return <Chip size="small" label={fkName} onClick={() => navigate(route)} clickable color="primary" variant="outlined" />;
      }
      return <Box component="pre" sx={{ m: 0, fontSize: '0.78rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(value, null, 2)}</Box>;
    }
    if (Array.isArray(value)) {
      return <Box component="pre" sx={{ m: 0, fontSize: '0.78rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(value, null, 2)}</Box>;
    }
    if (typeof value === 'boolean') {
      return <Chip size="small" label={value ? t('agent.yes', { defaultValue: 'Yes' }) : t('agent.no', { defaultValue: 'No' })} color={value ? 'success' : 'default'} variant="outlined" />;
    }
    // Known enum column (e.g. tenders.type, fund_system.payment_status) -> translated chip.
    const chip = enumChip(key, value, isRTL ? 'ar' : 'en');
    if (chip) {
      return <Chip size="small" label={chip.label} color={chip.color} variant="outlined" />;
    }
    if (config.linkFields?.includes(key) && /^https?:\/\//.test(String(value))) {
      return (
        <Button size="small" variant="outlined" startIcon={<OpenInNew fontSize="small" />} href={String(value)} target="_blank" rel="noopener noreferrer">
          {t('agent.open', { defaultValue: 'Open' })}
        </Button>
      );
    }
    if (isDateField(key)) return <Typography variant="body2">{formatDate(value, isRTL ? 'ar' : 'en', 'long')}</Typography>;
    return <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{String(value)}</Typography>;
  };

  const fileFieldSet = new Set(config.fileFields || []);
  const allFields = record
    ? Object.keys(record).filter((k) => !HIDDEN_FIELDS.has(k) && !fileFieldSet.has(k))
    : [];
  const coreFields = config.coreFields
    ? config.coreFields.filter((k) => allFields.includes(k))
    : allFields;
  const restFields = config.coreFields
    ? allFields.filter((k) => !config.coreFields!.includes(k))
    : [];

  // Every attachment for this record, unified: the single legacy file column
  // (if any) shown first, then each row of the related attachments table.
  const combinedAttachments: Array<{ id: string; url: string; label: string }> = [];
  if (record) {
    for (const f of config.fileFields || []) {
      if (record[f]) combinedAttachments.push({ id: `field:${f}`, url: record[f], label: arabicColumnLabel(f) });
    }
  }
  for (const att of attachments) {
    const url = att[config.attachments!.fileField];
    if (url) combinedAttachments.push({ id: att.id, url, label: att[config.attachments!.labelField] || url });
  }

  const downloadPdf = () => {
    if (!record) return;
    // Flatten the record into Arabic key/value rows for a branded PDF (Arabic by default).
    const FIELD = 'الحقل';
    const VALUE = 'القيمة';
    const flat = [...coreFields, ...restFields].map((k) => ({
      [FIELD]: arabicColumnLabel(k),
      [VALUE]: typeof record[k] === 'object' ? JSON.stringify(record[k]) : String(record[k] ?? ''),
    }));
    exportRowsToPdf(flat, [FIELD, VALUE], {
      title: `${arabicEntityLabel(entity)}: ${record[config.titleField] || id}`,
      filenameBase: `PCU_${entity}_${id}`,
    });
  };

  const renderFieldGrid = (keys: string[]) => (
    <Grid container spacing={2}>
      {keys.map((key) => {
        const isHtml = config.htmlFields?.includes(key);
        return (
          <Grid size={isHtml ? { xs: 12 } : { xs: 12, sm: 6, md: 4 }} key={key}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
              {isRTL ? arabicColumnLabel(key) : prettyHeader(key)}
            </Typography>
            {renderValue(key, record[key])}
          </Grid>
        );
      })}
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate(-1)} size="small"><BackIcon /></IconButton>
        <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
          {entityLabel}
        </Typography>
        {record && (
          <>
            <Button size="small" variant="contained" startIcon={<Edit fontSize="small" />} onClick={() => navigate(`/${entity.replace('_', '-')}/${id}/edit`)}>
              {t('table.edit')}
            </Button>
            <Tooltip title={t('agent.downloadPdf')}>
              <Button size="small" variant="outlined" color="error" startIcon={<Download fontSize="small" />} onClick={downloadPdf}>
                PDF
              </Button>
            </Tooltip>
          </>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : !record ? (
        <Alert severity="warning">{t('agent.noResults')}</Alert>
      ) : (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              {record[config.titleField] || `#${id}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">{t('common.id')}: {id}</Typography>
            <Divider sx={{ my: 2 }} />

            {renderFieldGrid(coreFields)}

            {restFields.length > 0 && (
              <>
                <Divider sx={{ my: 2 }}>
                  <Chip label={t('common.moreDetails')} size="small" variant="outlined" />
                </Divider>
                {renderFieldGrid(restFields)}
              </>
            )}

            {config.attachments && (
              <Accordion
                defaultExpanded={combinedAttachments.length > 0}
                disableGutters
                elevation={0}
                sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <AttachFile fontSize="small" color="action" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, flexGrow: 1 }}>
                      {t('common.attachments')} ({combinedAttachments.length})
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                    <Button
                      size="small"
                      startIcon={<Add fontSize="small" />}
                      onClick={(e) => { e.stopPropagation(); setAttachmentDialogOpen(true); }}
                    >
                      {t('common.addAttachment')}
                    </Button>
                  </Box>
                  {combinedAttachments.length === 0 ? (
                    <Typography variant="body2" color="text.disabled">{t('common.noAttachments')}</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {combinedAttachments.map((att) => (
                        <Box key={att.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                              {att.label}
                            </Typography>
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                              href={att.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ minWidth: 0, py: 0 }}
                            >
                              {t('agent.open', { defaultValue: 'Open' })}
                            </Button>
                          </Box>
                          {isPdfUrl(att.url) ? (
                            <Box
                              component="iframe"
                              src={att.url}
                              title={att.label}
                              sx={{ width: '100%', height: 460, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                            />
                          ) : isImageUrl(att.url) ? (
                            <Box
                              component="img"
                              src={att.url}
                              alt={att.label}
                              sx={{ maxWidth: '100%', maxHeight: 460, borderRadius: 1, border: '1px solid', borderColor: 'divider', display: 'block' }}
                            />
                          ) : (
                            <Box sx={{ p: 2, borderRadius: 1, border: '1px dashed', borderColor: 'divider', textAlign: 'center' }}>
                              <AttachFile color="action" />
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            )}
          </CardContent>
        </Card>
      )}

      {config.attachments && (
        <DetailDialog
          open={attachmentDialogOpen}
          onClose={() => setAttachmentDialogOpen(false)}
          tableName={config.attachments.table}
          initialData={{ [config.attachments.parentField]: id }}
          fields={(getEntityDraftFields(config.attachments.table, isRTL) || []) as any}
          onSave={() => { setAttachmentDialogOpen(false); fetchAttachments(); }}
        />
      )}
    </Box>
  );
};

export default EntityDetailPage;
